/**
 * rev 17b — the bulk-bar confirm dialog: one of the two danger actions ("ปลด license" / "เอาออกจากบัญชี",
 * F5 pinned Thai copy — see i18n) applied to every row currently selected. Selection is PAGE-SCOPED
 * everywhere (PoolUsersPage's own Set, cleared on page/filter/search change — F6), so this dialog always
 * receives a bounded list (at most one page, ≤100) even though the server's own interactive cap
 * (`POOL_BULK_CHUNK_SIZE`, hooks.ts — mirrors `pool_expiry_blast_cap`) is smaller: the confirm→submit
 * step below CHUNKS the selection into sequential `bulk-force` calls of at most that size, aggregating
 * every chunk's flat `{email: code}` map into one results panel with a running progress line
 * ("กำลังดำเนินการ 21–30 จาก 34…").
 *
 * A request-level abort (409/503/422 — bulkForceAbort.ts) STOPS the chain; a network/5xx failure
 * (isPartialCrashError) also stops it but renders the F4 "some may already have gone through" note
 * instead, because the server's per-email commit means the in-flight chunk may have partially landed.
 * Either way whatever results DID arrive before the stop are still shown and still converge the users
 * cache (convergeUsersAfterBulkForce) — a partial run is not discarded.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ApiRequestError } from "@/api/client";
import { Button, Input, Modal, StatusPill } from "@/components/ui";
import {
  POOL_BULK_CHUNK_SIZE,
  convergeUsersAfterBulkForce,
  poolKeys,
  useBulkForce,
} from "@/features/pool/hooks";
import { bucket, outcomeText, rank } from "@/features/pool/forceOutcome";
import { bulkForceAbortMessage, isPartialCrashError } from "@/features/pool/bulkForceAbort";
import { PoolWriteModeNotice, usePoolWriteIsLive } from "@/features/pool/PoolWriteModeNotice";
import type { PoolBulkForceAction, PoolUser } from "@/api/types";
import type { TKey } from "@/i18n/keys";

function errMessage(e: unknown, fallback: string): string {
  return e instanceof ApiRequestError ? e.error.message : fallback;
}

const TITLE_KEY: Record<PoolBulkForceAction, TKey> = {
  license_remove: "pool.users.bulk.confirmTitleLicenseRemove",
  unlink: "pool.users.bulk.confirmTitleUnlink",
};
const BODY_KEY: Record<PoolBulkForceAction, TKey> = {
  license_remove: "pool.users.bulk.confirmBodyLicenseRemove",
  unlink: "pool.users.bulk.confirmBodyUnlink",
};
const CONFIRM_BUTTON_KEY: Record<PoolBulkForceAction, TKey> = {
  license_remove: "pool.users.bulk.confirmButtonLicenseRemove",
  unlink: "pool.users.bulk.confirmButtonUnlink",
};

export interface BulkForceDialogProps {
  action: PoolBulkForceAction;
  /** The full selected rows (not just emails) — carries `standalone`/`mirror_ready` for the confirm
   *  warnings and `email` for the chunked calls + the scrollable preview list. */
  users: PoolUser[];
  onClose: () => void;
}

type Phase = "confirm" | "running" | "done";

export function BulkForceDialog({ action, users, onClose }: BulkForceDialogProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const mut = useBulkForce();
  const isLive = usePoolWriteIsLive();

  const [phase, setPhase] = useState<Phase>("confirm");
  const [progress, setProgress] = useState<{ from: number; to: number; total: number } | null>(
    null,
  );
  const [results, setResults] = useState<Record<string, string>>({});
  const [dryRun, setDryRun] = useState(false);
  const [abortMessage, setAbortMessage] = useState<string | null>(null);
  const [partialFailure, setPartialFailure] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [countText, setCountText] = useState("");

  const emails = users.map((u) => u.email);
  const anyStandalone = users.some((u) => u.standalone);
  const anyMirrorPending = users.some((u) => u.mirror_ready === false);

  // Typed-count ceremony scales with blast radius: a LIVE bulk write (many rows, one click) makes the
  // operator retype the exact selected count before confirm unlocks. In test/off mode nothing will
  // actually happen, so demanding it there would be dishonest theatre — the gate is live-only.
  const requiresTypedCount = isLive === true;
  const typedCountMatches = countText.trim() === String(emails.length);
  // R1 fail-safe (adversarial review): isLive === null/undefined means the write mode is UNKNOWN
  // (system-state query still loading or errored). Unknown is NOT "off": we must not fire an un-gated
  // real bulk write under the "nothing happens" body, so the confirm button is DISABLED here and the
  // body swaps to a neutral "checking" line — never the definite "system is off / no real effect" claim.
  const writeModeUnknown = isLive !== true && isLive !== false;

  async function run() {
    setPhase("running");
    const merged: Record<string, string> = {};
    let dryRunFlag = false;
    const total = emails.length;
    for (let start = 0; start < total; start += POOL_BULK_CHUNK_SIZE) {
      const chunk = emails.slice(start, start + POOL_BULK_CHUNK_SIZE);
      setProgress({ from: start + 1, to: Math.min(start + chunk.length, total), total });
      try {
        // Deliberately sequential (F6: aggregate progress "processing X–Y of N…") — not a lint-flagged
        // await-in-loop pattern in this repo's config, but noted here for the same reason it usually is.
        const res = await mut.mutateAsync({ action, emails: chunk });
        Object.assign(merged, res.results);
        dryRunFlag = res.dry_run;
      } catch (e) {
        const abort = bulkForceAbortMessage(e, t);
        if (abort) setAbortMessage(abort);
        else if (isPartialCrashError(e)) setPartialFailure(true);
        else setAbortMessage(errMessage(e, t("state.errorTitle")));
        finish(merged, dryRunFlag);
        return;
      }
    }
    finish(merged, dryRunFlag);
  }

  function finish(merged: Record<string, string>, dryRunFlag: boolean) {
    setResults(merged);
    setDryRun(dryRunFlag);
    setPhase("done");
    void qc.invalidateQueries({ queryKey: poolKeys.dashboard });
    void qc.invalidateQueries({ queryKey: poolKeys.intents });
    if (!convergeUsersAfterBulkForce(qc, merged, dryRunFlag, action)) {
      void qc.invalidateQueries({ queryKey: ["pool", "users"] });
    }
  }

  const entries = Object.entries(results);
  const counts = entries.reduce(
    (acc, [, code]) => {
      acc[bucket(code)] += 1;
      return acc;
    },
    { ok: 0, skip: 0, failed: 0 },
  );
  const sortedEntries = [...entries].sort(
    ([, a], [, b]) => rank(a) - rank(b) || a.localeCompare(b),
  );

  return (
    <Modal
      open
      onClose={phase === "running" ? () => undefined : onClose}
      title={t(TITLE_KEY[action], { count: emails.length })}
      size="md"
      footer={
        phase === "confirm" ? (
          <>
            <Button variant="secondary" onClick={onClose}>
              {t("action.cancel")}
            </Button>
            {/* R1 late ruling: danger tone ONLY once the system's write mode is CONFIRMED live —
                `isLive === false` (off/dry-run) and `null` (still loading/errored) both fall through
                to the neutral tone, never the reverse. The old `isLive === false ? secondary : danger`
                defaulted an unconfirmed/unknown state to danger, which is the unsafe direction.
                Fail-safe: while the write mode is UNKNOWN the confirm is also DISABLED (writeModeUnknown)
                so an un-gated real write can never fire before the state is known. */}
            <Button
              variant={isLive === true ? "danger" : "secondary"}
              disabled={writeModeUnknown || (requiresTypedCount && !typedCountMatches)}
              onClick={() => void run()}
            >
              {t(CONFIRM_BUTTON_KEY[action])}
            </Button>
          </>
        ) : phase === "done" ? (
          <Button variant="secondary" onClick={onClose}>
            {t("action.close")}
          </Button>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-3">
        {phase === "confirm" && (
          <>
            <PoolWriteModeNotice />
            {/* F1 body honesty (R1 late ruling + fail-safe): three honest states, never a lie.
                true → the live-danger "happens immediately" body. false → the shared "system is off"
                test-mode body. null/undefined (loading/errored) → a NEUTRAL "checking write mode" line,
                NOT the definite "off / no real effect" claim, because we don't yet know it's off. */}
            <p className="text-(length:--text-sm) text-(--text)">
              {isLive === true
                ? t(BODY_KEY[action])
                : isLive === false
                  ? t("pool.period.force.testModeBody")
                  : t("pool.period.force.checkingWriteMode")}
            </p>
            {anyStandalone && (
              <p className="text-(length:--text-2xs) font-medium text-(--info)" role="note">
                {t("pool.users.standaloneWarning")}
              </p>
            )}
            {anyMirrorPending && (
              <p className="text-(length:--text-2xs) font-medium text-(--info)" role="note">
                {t("pool.period.force.mirrorPendingNote")}
              </p>
            )}
            {/* F6 — every selected email, full stop: no "and N more" truncation on an irreversible
                bulk write. Scrolls inside its own box rather than growing the modal unbounded. */}
            <div className="flex flex-col gap-1.5">
              <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
                {t("pool.users.bulk.emailListLabel", { count: emails.length })}
              </span>
              <ul className="max-h-48 overflow-y-auto rounded-(--radius) border border-(--border) bg-(--surface-sunken) p-2 text-(length:--text-sm)">
                {emails.map((email) => (
                  <li key={email} className="px-1 py-0.5 text-(--text)">
                    {email}
                  </li>
                ))}
              </ul>
            </div>
            {/* R1 — typed-count gate, LIVE only: the operator must retype the exact selected count
                before the confirm button unlocks. Only rendered when isLive is CONFIRMED true (in
                off/unknown mode nothing happens, so a gate there would be dishonest). Mirrors
                PoolSystemPage's type-"LIVE" ceremony. */}
            {requiresTypedCount && (
              <label className="flex flex-col gap-1.5">
                <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
                  {t("pool.users.bulk.typeCountLabel", { count: emails.length })}
                </span>
                <Input
                  value={countText}
                  onChange={(e) => setCountText(e.target.value)}
                  placeholder={t("pool.users.bulk.typeCountPlaceholder", { count: emails.length })}
                  inputMode="numeric"
                  autoComplete="off"
                  spellCheck={false}
                />
              </label>
            )}
          </>
        )}

        {phase === "running" && progress && (
          <p className="text-(length:--text-sm) text-(--text-muted)" role="status">
            {t("pool.users.bulk.progressLabel", {
              from: progress.from,
              to: progress.to,
              total: progress.total,
            })}
          </p>
        )}

        {abortMessage && (
          <p role="alert" className="text-(length:--text-sm) text-(--danger)">
            {abortMessage}
          </p>
        )}
        {partialFailure && (
          <p role="alert" className="text-(length:--text-sm) text-(--danger)">
            {t("pool.users.bulk.partialFailureNote")}
            <Link to="/pool/intents" className="underline underline-offset-2">
              {t("pool.tabs.intents")}
            </Link>
          </p>
        )}

        {phase === "done" && (
          <div className="flex flex-col gap-2 rounded-(--radius) border border-(--border) bg-(--surface-sunken) p-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-(length:--text-sm) font-semibold text-(--text-strong)">
                {t("pool.period.force.resultTitle")}
              </p>
              <StatusPill
                tone={
                  dryRun ? "warn" : counts.ok > 0 ? "ok" : counts.failed > 0 ? "danger" : "neutral"
                }
              >
                {dryRun
                  ? t("pool.period.force.dryRunBadge")
                  : counts.ok > 0
                    ? t("pool.period.force.liveBadge")
                    : t("pool.period.force.noWriteBadge")}
              </StatusPill>
            </div>
            <p className="text-(length:--text-sm) text-(--text)">
              {t("pool.users.bulk.resultSummary", {
                ok: counts.ok,
                skipped: counts.skip,
                failed: counts.failed,
              })}
            </p>
            {sortedEntries.length > 0 && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="self-start"
                  onClick={() => setDetailsOpen((v) => !v)}
                >
                  {detailsOpen
                    ? t("pool.users.bulk.hideDetailsButton")
                    : t("pool.users.bulk.showDetailsButton")}
                </Button>
                {detailsOpen && (
                  <ul className="flex max-h-64 flex-col gap-1 overflow-y-auto text-(length:--text-sm)">
                    {sortedEntries.map(([email, code]) => (
                      <li key={email} className="flex items-center justify-between gap-3">
                        <span className="text-(--text)">{email}</span>
                        <span className="text-(--text-muted)">{outcomeText(code, t)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
